import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, LucideIcon } from "lucide-react";
import { MagicCard } from "../ui/magic-card";
import { Button } from "../ui/button";
import { cn } from "@/lib";
import { getFeatureColors } from "@/lib/design-tokens";

interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  features?: string[];
  href?: string;
  color?: string;
  variant?: "default" | "compact" | "detailed";
  animated?: boolean;
  index?: number;
  className?: string;
}

const FeatureCard = ({
  icon: Icon,
  title,
  description,
  features,
  href,
  color = "text-blue-500",
  variant = "default",
  animated = true,
  index = 0,
  className,
}: FeatureCardProps) => {
  const featureColors = getFeatureColors(color);

  if (variant === "compact") {
    return (
      <motion.div
        className={cn(
          "p-4 rounded-xl border bg-card hover:shadow-md transition-all duration-300",
          className,
        )}
        initial={animated ? { opacity: 0, y: 20 } : {}}
        whileInView={animated ? { opacity: 1, y: 0 } : {}}
        transition={animated ? { duration: 0.5, delay: index * 0.1 } : {}}
        viewport={{ once: true }}
        data-oid="okf70wz"
      >
        <div className="flex items-start gap-3" data-oid="7m8ug8:">
          <Check
            className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0"
            data-oid="a50ddj2"
          />
          <span className="text-sm" data-oid=".d:hpv-">
            {title}
          </span>
        </div>
      </motion.div>
    );
  }

  const cardContent = (
    <MagicCard
      gradientFrom="#3b82f6"
      gradientTo="#8b5cf6"
      gradientColor="rgba(59,130,246,0.05)"
      className={cn(
        "group p-6 rounded-xl border bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 h-full",
        href && "cursor-pointer",
        className,
      )}
      data-oid="k.pp1c0"
    >
      {/* Icon */}
      <div
        className={cn(
          "w-12 h-12 rounded-lg p-2 mb-4 group-hover:scale-110 transition-transform duration-300",
          featureColors.bg,
        )}
        data-oid="__n0b16"
      >
        <Icon
          className={cn("w-full h-full", featureColors.text)}
          data-oid="rc74ou6"
        />
      </div>

      {/* Content */}
      <h3
        className="text-lg font-semibold mb-2 group-hover:text-blue-600 transition-colors"
        data-oid="nliy992"
      >
        {title}
      </h3>
      <p
        className="text-muted-foreground mb-4 text-sm leading-relaxed"
        data-oid="rqk5i5t"
      >
        {description}
      </p>

      {/* Features List */}
      {features && variant === "detailed" && (
        <div className="space-y-2 mb-4" data-oid="y-oni-u">
          {features.slice(0, 3).map((feature, idx) => (
            <motion.div
              key={idx}
              className="flex items-start gap-2 text-sm text-muted-foreground"
              initial={animated ? { opacity: 0, x: -20 } : {}}
              whileInView={animated ? { opacity: 1, x: 0 } : {}}
              transition={animated ? { delay: idx * 0.1 } : {}}
              viewport={{ once: true }}
              data-oid="t9cyo6:"
            >
              <Check
                className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0"
                data-oid="dc0y90f"
              />
              {feature}
            </motion.div>
          ))}
        </div>
      )}

      {/* Action Button */}
      {href && (
        <Button
          variant="outline"
          className="w-full group-hover:bg-blue-50 group-hover:border-blue-200 transition-all duration-300"
          data-oid="xfvdr2."
        >
          Learn More
          <ArrowRight
            className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
            data-oid="cv7getc"
          />
        </Button>
      )}
    </MagicCard>
  );

  const motionWrapper = (
    <motion.div
      className="h-full"
      initial={animated ? { opacity: 0, y: 20 } : {}}
      whileInView={animated ? { opacity: 1, y: 0 } : {}}
      transition={animated ? { duration: 0.5, delay: index * 0.1 } : {}}
      viewport={{ once: true }}
      whileHover={animated ? { y: -5 } : {}}
      data-oid="4u2ifzl"
    >
      {cardContent}
    </motion.div>
  );

  return href ? (
    <Link to={href} className="block h-full" data-oid="g-7f2s0">
      {motionWrapper}
    </Link>
  ) : (
    motionWrapper
  );
};

export default FeatureCard;
